import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import useSubscriptionPricing from "common/hooks/useSubscriptionPricing";
import type {
  PricingPeriodUnit,
  RentedSubscription,
  Subscription,
  SubscriptionType,
} from "common/types";

import { post } from "../helpers/HTTP";

async function estimateUpdateRequest(
  workspaceId: string,
  subscriptionId: string,
  planId: string,
  licenseId: string,
  quantity: number,
  prorate: boolean,
): Promise<{ amountDue: number; nextBillingAt: number }> {
  const { estimate } = await post(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/estimates/update`,
    {
      items: [
        {
          id: planId,
        },
        ...(quantity > 0
          ? [
              {
                id: licenseId,
                quantity,
              },
            ]
          : []),
      ],
      prorate,
    },
  );

  const {
    invoice_estimate: invoiceEstimate,
    subscription_estimate: subscriptionEstimate,
    next_invoice_estimate: nextInvoiceEstimate,
  } = estimate;

  return {
    amountDue: invoiceEstimate
      ? invoiceEstimate.amount_due
      : nextInvoiceEstimate.amount_due,
    nextBillingAt: subscriptionEstimate.next_billing_at,
  };
}

export default function useEstimateUpdate({
  selectedPeriodUnit,
  quantity,
  prorate,
  subscription,
  type,
  isDisabled = false,
}: {
  selectedPeriodUnit: PricingPeriodUnit;
  quantity: number;
  prorate: boolean;
  subscription: Subscription | RentedSubscription;
  type: SubscriptionType;
  isDisabled?: boolean;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { plan, license } = useSubscriptionPricing(selectedPeriodUnit, type);

  const { data: estimateUpdate, isLoading: isLoadingEstimateUpdate } = useQuery(
    {
      queryKey: [
        "estimateUpdate",
        workspaceId,
        subscription?.id,
        plan.id,
        license.id,
        selectedPeriodUnit,
        type,
        quantity,
        prorate,
      ],
      queryFn: () =>
        estimateUpdateRequest(
          workspaceId,
          subscription?.id,
          plan.id,
          license.id,
          quantity,
          prorate,
        ),
      enabled: !isDisabled && !!subscription, // The request should only be called if there is a subscription
    },
  );

  return { estimateUpdate, isLoadingEstimateUpdate };
}
